import { useCallback, useEffect, useId, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, HeartPulse, ShieldAlert, type LucideIcon } from "lucide-react";

import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { VoiceToTextMicButton } from "@/components/ui/VoiceToTextMicButton";
import { ApiError } from "@/lib/api";
import {
  MANUFACTURING_SAFETY_REPORTS_QUERY_KEY,
  PERSONAL_SAFETY_REPORTS_QUERY_KEY,
  SAFETY_MEDICAL_ATTENTION_LABELS,
  SAFETY_MEDICAL_ATTENTION_LEVELS,
  SAFETY_REPORT_CATEGORIES,
  SAFETY_REPORT_CATEGORY_LABELS,
  SAFETY_REPORT_KIND_LABELS,
  SAFETY_REPORT_SEVERITIES,
  SAFETY_REPORT_SEVERITY_LABELS,
  SAFETY_REPORT_STATUS_LABELS,
  SAFETY_WORK_AREA_OPTIONS,
  createSafetyReport,
  fetchMySafetyReportDetail,
  fetchMySafetyReports,
  postMySafetyReportMessage,
  safetyReportKindTone,
  safetyReportMessagesAsHubThread,
  safetyReportSeverityTone,
  safetyReportStatusIsTerminal,
  safetyReportStatusTone,
  type SafetyMedicalAttentionLevel,
  type SafetyReportCategory,
  type SafetyReportKind,
  type SafetyReportSeverity,
} from "@/lib/safetyReportsApi";
import { cn, formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Report safety concerns, near misses, and safety incidents or injuries on the shop floor or in the office. Manufacturing Management and the safety notification list are alerted immediately and track containment, root cause, and corrective actions here.";

const SAFETY_REPORT_KIND_CARD_META: ReadonlyArray<{
  kind: SafetyReportKind;
  overviewTitle: string;
  createTitle: string;
  description: string;
  Icon: LucideIcon;
  iconContainerClassName: string;
  hoverBorderClassName: string;
  selectedClassName: string;
}> = [
  {
    kind: "concern",
    overviewTitle: "Report a safety concern",
    createTitle: "Safety concern",
    description:
      "Unsafe condition, equipment hazard, ergonomics, housekeeping, or missing protective gear.",
    Icon: AlertTriangle,
    iconContainerClassName: "bg-amber-100 text-amber-800",
    hoverBorderClassName: "hover:border-brand-300 hover:bg-slate-50",
    selectedClassName:
      "border-brand-600 bg-brand-50/70 ring-1 ring-brand-600",
  },
  {
    kind: "near_miss",
    overviewTitle: "Report a near miss",
    createTitle: "Near miss",
    description:
      "A close call where no injury happened, but a change is needed so nobody gets hurt next time.",
    Icon: ShieldAlert,
    iconContainerClassName: "bg-sky-100 text-sky-800",
    hoverBorderClassName: "hover:border-brand-300 hover:bg-slate-50",
    selectedClassName:
      "border-brand-600 bg-brand-50/70 ring-1 ring-brand-600",
  },
  {
    kind: "incident",
    overviewTitle: "Report an incident or injury",
    createTitle: "Incident / Injury",
    description:
      "Injury, burn, cut, chemical exposure, first aid, medical visit, or equipment damage.",
    Icon: HeartPulse,
    iconContainerClassName: "bg-rose-100 text-rose-800",
    hoverBorderClassName: "hover:border-rose-300 hover:bg-rose-50/40",
    selectedClassName:
      "border-rose-600 bg-rose-50/60 ring-1 ring-rose-600",
  },
];

function currentLocalDateTimeInputValue(): string {
  const now = new Date();
  const offsetMilliseconds = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMilliseconds).toISOString().slice(0, 16);
}

function SafetyReportListPage(): JSX.Element {
  const navigate = useNavigate();
  const listQuery = useQuery({
    queryKey: [...PERSONAL_SAFETY_REPORTS_QUERY_KEY, "mine"],
    queryFn: () => fetchMySafetyReports(),
  });
  const rows = listQuery.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Safety report</PageTitleWithHelp>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        {SAFETY_REPORT_KIND_CARD_META.map((card) => {
          const CardIcon = card.Icon;
          return (
            <button
              key={card.kind}
              type="button"
              onClick={() => navigate(`/personal/safety-reports/new?kind=${card.kind}`)}
              className={cn(
                "flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors",
                card.hoverBorderClassName,
              )}
            >
              <span className={cn("mt-0.5 rounded-md p-2", card.iconContainerClassName)}>
                <CardIcon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <div className="text-sm font-semibold text-slate-900">{card.overviewTitle}</div>
                <p className="mt-1 text-xs text-slate-600">{card.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your submitted safety reports</CardTitle>
        </CardHeader>
        <CardBody className="p-0 sm:p-0">
          {listQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading your safety reports...</p>
          ) : listQuery.isError ? (
            <p className="px-5 py-4 text-sm text-red-600">{(listQuery.error as Error).message}</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-600">
              You have not submitted any safety reports yet. Choose one of the cards above to log a concern, near miss,
              or incident.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    to={`/personal/safety-reports/${encodeURIComponent(row.id)}`}
                    className="flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-slate-50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">
                        Report #{row.id} · {row.title}
                      </span>
                      <Badge tone={safetyReportKindTone(row.report_kind)}>
                        {SAFETY_REPORT_KIND_LABELS[row.report_kind]}
                      </Badge>
                      <Badge tone={safetyReportSeverityTone(row.severity)}>
                        {SAFETY_REPORT_SEVERITY_LABELS[row.severity]}
                      </Badge>
                      <Badge tone={safetyReportStatusTone(row.status)}>
                        {SAFETY_REPORT_STATUS_LABELS[row.status]}
                      </Badge>
                      {row.pending_feedback ? (
                        <Badge tone="warning">Feedback requested</Badge>
                      ) : null}
                      {(row.message_count ?? 0) > 0 ? (
                        <span className="text-xs text-slate-500">
                          {String(row.message_count)} message{row.message_count === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>
                    <p className="line-clamp-2 text-sm text-slate-700">{row.description}</p>
                    <p className="text-xs text-slate-500">
                      {row.work_area} · Submitted {formatTimestamp(row.created_at)}
                      {row.assigned_to_display_name ? ` · Assigned to ${row.assigned_to_display_name}` : ""}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function SafetyReportNewPage(): JSX.Element {
  const formId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [searchParams] = useSearchParams();
  const initialKindParam = searchParams.get("kind");
  const initialKind: SafetyReportKind =
    initialKindParam === "near_miss" || initialKindParam === "incident" ? initialKindParam : "concern";

  const [reportKind, setReportKind] = useState<SafetyReportKind>(initialKind);
  const [severity, setSeverity] = useState<SafetyReportSeverity>(
    initialKind === "incident" ? "medium" : "low",
  );
  const [category, setCategory] = useState<SafetyReportCategory>("hazard_unsafe_condition");
  const [workArea, setWorkArea] = useState<string>("Assembly");
  const [occurredAt, setOccurredAt] = useState<string>(() => currentLocalDateTimeInputValue());
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [immediateActionTaken, setImmediateActionTaken] = useState("");
  const [suggestedPrevention, setSuggestedPrevention] = useState("");
  const [equipmentInvolved, setEquipmentInvolved] = useState("");
  const [witnesses, setWitnesses] = useState("");
  const [injuredPersonName, setInjuredPersonName] = useState("");
  const [medicalAttentionLevel, setMedicalAttentionLevel] =
    useState<SafetyMedicalAttentionLevel>("none");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialKindParam === "concern" || initialKindParam === "near_miss" || initialKindParam === "incident") {
      setReportKind(initialKindParam);
      if (initialKindParam === "incident" && severity === "low") {
        setSeverity("medium");
      }
    }
  }, [initialKindParam]);

  const onSubmit = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    try {
      const occurredIso =
        occurredAt.trim() !== "" ? new Date(occurredAt).toISOString() : new Date().toISOString();
      const isIncident = reportKind === "incident";
      const showWitnesses = reportKind !== "concern";
      const result = await createSafetyReport({
        report_kind: reportKind,
        severity,
        category,
        title: title.trim(),
        work_area: workArea.trim() || "Other",
        occurred_at: occurredIso,
        description: description.trim(),
        immediate_action_taken: immediateActionTaken.trim() || null,
        suggested_prevention: suggestedPrevention.trim() || null,
        equipment_involved: equipmentInvolved.trim() || null,
        witnesses: showWitnesses ? witnesses.trim() || null : null,
        injured_person_name: isIncident ? injuredPersonName.trim() || null : null,
        medical_attention_level: isIncident ? medicalAttentionLevel : "none",
      });
      void queryClient.invalidateQueries({ queryKey: PERSONAL_SAFETY_REPORTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: MANUFACTURING_SAFETY_REPORTS_QUERY_KEY });
      showToast({
        tone: "success",
        message: `Safety report #${result.id} was submitted.`,
      });
      navigate(`/personal/safety-reports/${encodeURIComponent(result.id)}`);
    } catch (caughtError) {
      const message =
        caughtError instanceof ApiError
          ? caughtError.message
          : caughtError instanceof Error
            ? caughtError.message
            : "Could not submit safety report.";
      showToast({ tone: "error", message });
    } finally {
      setSubmitting(false);
    }
  }, [
    category,
    description,
    equipmentInvolved,
    immediateActionTaken,
    injuredPersonName,
    medicalAttentionLevel,
    navigate,
    occurredAt,
    queryClient,
    reportKind,
    severity,
    showToast,
    suggestedPrevention,
    title,
    witnesses,
    workArea,
  ]);

  const canSubmit = title.trim() !== "" && description.trim() !== "" && workArea.trim() !== "";

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HELP} breadcrumbLabel="New safety report">
          New safety report
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardBody>
          <form
            id={formId}
            className="space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSubmit || submitting) return;
              void onSubmit();
            }}
          >
            <div>
              <Label>What are you reporting?</Label>
              <div className="mt-1.5 grid gap-3 sm:grid-cols-3">
                {SAFETY_REPORT_KIND_CARD_META.map((card) => {
                  const selected = reportKind === card.kind;
                  const CardIcon = card.Icon;
                  return (
                    <button
                      key={card.kind}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setReportKind(card.kind);
                        if (card.kind === "incident" && severity === "low") {
                          setSeverity("medium");
                        }
                      }}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-4 text-left shadow-sm transition-colors",
                        selected
                          ? card.selectedClassName
                          : cn("border-slate-200 bg-white", card.hoverBorderClassName),
                      )}
                    >
                      <span className={cn("mt-0.5 rounded-md p-2", card.iconContainerClassName)}>
                        <CardIcon className="h-5 w-5" aria-hidden />
                      </span>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{card.createTitle}</div>
                        <p className="mt-1 text-xs text-slate-600">{card.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label htmlFor={`${formId}-work-area`}>Work area or station</Label>
                <div className="mt-1">
                  <HubTouchSelect
                    id={`${formId}-work-area`}
                    value={workArea}
                    onChange={(nextValue) => setWorkArea(nextValue)}
                    options={[...SAFETY_WORK_AREA_OPTIONS]}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`${formId}-category`}>Category</Label>
                <div className="mt-1">
                  <HubTouchSelect
                    id={`${formId}-category`}
                    value={category}
                    onChange={(nextValue) => setCategory(nextValue as SafetyReportCategory)}
                    options={SAFETY_REPORT_CATEGORIES.map((value) => ({
                      value,
                      label: SAFETY_REPORT_CATEGORY_LABELS[value],
                    }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`${formId}-severity`}>Severity</Label>
                <div className="mt-1">
                  <HubTouchSelect
                    id={`${formId}-severity`}
                    value={severity}
                    onChange={(nextValue) => setSeverity(nextValue as SafetyReportSeverity)}
                    options={SAFETY_REPORT_SEVERITIES.map((value) => ({
                      value,
                      label: SAFETY_REPORT_SEVERITY_LABELS[value],
                    }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`${formId}-occurred`}>
                  {reportKind === "concern" ? "When was this noticed?" : "When did this happen?"}
                </Label>
                <Input
                  id={`${formId}-occurred`}
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor={`${formId}-title`}>Short summary</Label>
              <Input
                id={`${formId}-title`}
                required
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="mt-1"
                placeholder="For example: Frayed power cord on Cal+Test rack 2, or minor cut opening carton at Receiving"
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`${formId}-description`}>
                  {reportKind === "concern"
                    ? "What hazard or unsafe condition did you observe?"
                    : "What happened?"}
                </Label>
                <VoiceToTextMicButton
                  value={description}
                  ariaLabel="Dictate description"
                  onValueChange={setDescription}
                />
              </div>
              <TextArea
                id={`${formId}-description`}
                required
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="mt-1"
                placeholder="Describe what happened, the exact bench/shelf/location, and any conditions involved..."
              />
            </div>

            {reportKind === "incident" ? (
              <div className="space-y-4 rounded-md border border-rose-200 bg-rose-50/40 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Injury and medical details
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor={`${formId}-injured-person`}>Person injured or involved (optional)</Label>
                    <Input
                      id={`${formId}-injured-person`}
                      value={injuredPersonName}
                      onChange={(event) => setInjuredPersonName(event.target.value)}
                      className="mt-1"
                      placeholder="Name of person injured or affected"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`${formId}-medical`}>Medical attention</Label>
                    <div className="mt-1">
                      <HubTouchSelect
                        id={`${formId}-medical`}
                        value={medicalAttentionLevel}
                        onChange={(nextValue) =>
                          setMedicalAttentionLevel(nextValue as SafetyMedicalAttentionLevel)
                        }
                        options={SAFETY_MEDICAL_ATTENTION_LEVELS.map((value) => ({
                          value,
                          label: SAFETY_MEDICAL_ATTENTION_LABELS[value],
                        }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className={cn("grid gap-4", reportKind !== "concern" ? "sm:grid-cols-2" : undefined)}>
              <div>
                <Label htmlFor={`${formId}-equipment`}>Equipment, tool, or chemical involved (optional)</Label>
                <Input
                  id={`${formId}-equipment`}
                  value={equipmentInvolved}
                  onChange={(event) => setEquipmentInvolved(event.target.value)}
                  className="mt-1"
                  placeholder="Bench ID, laser fixture, heat gun, pallet jack, chemical..."
                />
              </div>
              {reportKind !== "concern" ? (
                <div>
                  <Label htmlFor={`${formId}-witnesses`}>Witnesses or staff notified (optional)</Label>
                  <Input
                    id={`${formId}-witnesses`}
                    value={witnesses}
                    onChange={(event) => setWitnesses(event.target.value)}
                    className="mt-1"
                    placeholder="Names of witnesses or supervisor notified"
                  />
                </div>
              ) : null}
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`${formId}-immediate-action`}>
                  Immediate action or containment already taken (optional)
                </Label>
                <VoiceToTextMicButton
                  value={immediateActionTaken}
                  ariaLabel="Dictate immediate action"
                  onValueChange={setImmediateActionTaken}
                />
              </div>
              <TextArea
                id={`${formId}-immediate-action`}
                rows={2}
                value={immediateActionTaken}
                onChange={(event) => setImmediateActionTaken(event.target.value)}
                className="mt-1"
                placeholder="Unplugged machine, cordoned off aisle, cleaned spill, applied first aid..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`${formId}-prevention`}>
                  Suggested fix or prevention idea (optional)
                </Label>
                <VoiceToTextMicButton
                  value={suggestedPrevention}
                  ariaLabel="Dictate suggested prevention"
                  onValueChange={setSuggestedPrevention}
                />
              </div>
              <TextArea
                id={`${formId}-prevention`}
                rows={2}
                value={suggestedPrevention}
                onChange={(event) => setSuggestedPrevention(event.target.value)}
                className="mt-1"
                placeholder="How can we prevent this from happening again?"
              />
            </div>

            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate("/personal/safety-reports")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !canSubmit}>
                {submitting ? "Submitting..." : "Submit safety report"}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function SafetyReportDetailPage(): JSX.Element {
  const { id: reportId = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [replyDraft, setReplyDraft] = useState("");
  const [replyError, setReplyError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: [...PERSONAL_SAFETY_REPORTS_QUERY_KEY, "detail", reportId],
    queryFn: () => fetchMySafetyReportDetail(reportId),
    enabled: reportId !== "",
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) => postMySafetyReportMessage(reportId, body),
    onSuccess: () => {
      setReplyDraft("");
      setReplyError(null);
      void queryClient.invalidateQueries({
        queryKey: [...PERSONAL_SAFETY_REPORTS_QUERY_KEY, "detail", reportId],
      });
      void queryClient.invalidateQueries({
        queryKey: [...PERSONAL_SAFETY_REPORTS_QUERY_KEY, "mine"],
      });
      void queryClient.invalidateQueries({
        queryKey: MANUFACTURING_SAFETY_REPORTS_QUERY_KEY,
      });
    },
    onError: (error: Error) => {
      setReplyError(error.message);
    },
  });

  const report = detailQuery.data?.report;
  const messages = detailQuery.data?.messages ?? [];

  return (
    <div className="space-y-6">
      <header>
        <PageTitleWithHelp
          hoverHint={PAGE_HELP}
          breadcrumbLabel={report ? `Safety report #${report.id}` : "Safety report"}
        >
          {report ? `Safety report #${report.id}` : "Safety report"}
        </PageTitleWithHelp>
      </header>

      <Card>
        <CardBody className="space-y-5">
          {detailQuery.isLoading && !report ? (
            <p className="text-sm text-slate-500">Loading safety report...</p>
          ) : detailQuery.isError ? (
            <p className="text-sm text-red-600">{(detailQuery.error as Error).message}</p>
          ) : report == null ? (
            <p className="text-sm text-slate-600">Safety report not found.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={safetyReportKindTone(report.report_kind)}>
                  {SAFETY_REPORT_KIND_LABELS[report.report_kind]}
                </Badge>
                <Badge tone={safetyReportSeverityTone(report.severity)}>
                  Severity: {SAFETY_REPORT_SEVERITY_LABELS[report.severity]}
                </Badge>
                <Badge tone={safetyReportStatusTone(report.status)}>
                  {SAFETY_REPORT_STATUS_LABELS[report.status]}
                </Badge>
                {report.pending_feedback ? (
                  <Badge tone="warning">Feedback requested</Badge>
                ) : null}
                <span className="text-xs text-slate-500">
                  Submitted {formatTimestamp(report.created_at)}
                </span>
              </div>

              <h2 className="text-base font-semibold text-slate-900">{report.title}</h2>

              {report.pending_feedback ? (
                <div className="rounded-md border border-amber-200 bg-amber-50/90 p-3 text-sm text-amber-950">
                  Manufacturing Management requested additional information on this report. Reply in the message
                  thread below.
                </div>
              ) : null}

              {report.containment_action?.trim() ||
              report.corrective_action?.trim() ||
              report.resolution_note?.trim() ? (
                <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/80 p-3 text-sm text-emerald-950">
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                    Safety team response and actions
                  </div>
                  {report.containment_action?.trim() ? (
                    <div>
                      <span className="font-semibold">Containment: </span>
                      <span className="whitespace-pre-wrap">{report.containment_action}</span>
                    </div>
                  ) : null}
                  {report.corrective_action?.trim() ? (
                    <div>
                      <span className="font-semibold">Corrective action: </span>
                      <span className="whitespace-pre-wrap">{report.corrective_action}</span>
                    </div>
                  ) : null}
                  {report.resolution_note?.trim() ? (
                    <div>
                      <span className="font-semibold">Resolution summary: </span>
                      <span className="whitespace-pre-wrap">{report.resolution_note}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Category</dt>
                  <dd className="mt-1 text-slate-900">{SAFETY_REPORT_CATEGORY_LABELS[report.category]}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Work area</dt>
                  <dd className="mt-1 text-slate-900">{report.work_area}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Occurred at</dt>
                  <dd className="mt-1 text-slate-900">{formatTimestamp(report.occurred_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Assigned owner</dt>
                  <dd className="mt-1 text-slate-900">
                    {report.assigned_to_display_name?.trim() || "Unassigned"}
                  </dd>
                </div>
                {report.injured_person_name?.trim() ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Person injured / involved
                    </dt>
                    <dd className="mt-1 text-slate-900">{report.injured_person_name}</dd>
                  </div>
                ) : null}
                {report.medical_attention_level !== "none" || report.report_kind === "incident" ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Medical attention
                    </dt>
                    <dd className="mt-1 text-slate-900">
                      {SAFETY_MEDICAL_ATTENTION_LABELS[report.medical_attention_level]}
                    </dd>
                  </div>
                ) : null}
                {report.equipment_involved?.trim() ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Equipment or tool involved
                    </dt>
                    <dd className="mt-1 text-slate-900">{report.equipment_involved}</dd>
                  </div>
                ) : null}
                {report.witnesses?.trim() ? (
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Witnesses</dt>
                    <dd className="mt-1 text-slate-900">{report.witnesses}</dd>
                  </div>
                ) : null}
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Description</dt>
                  <dd className="mt-1 whitespace-pre-wrap rounded-md border border-slate-100 bg-slate-50 p-3 text-slate-800">
                    {report.description}
                  </dd>
                </div>
                {report.immediate_action_taken?.trim() ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Immediate action taken
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap text-slate-900">
                      {report.immediate_action_taken}
                    </dd>
                  </div>
                ) : null}
                {report.suggested_prevention?.trim() ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Suggested prevention
                    </dt>
                    <dd className="mt-1 whitespace-pre-wrap text-slate-900">
                      {report.suggested_prevention}
                    </dd>
                  </div>
                ) : null}
              </dl>

              <HubSupportTicketMessageThread
                messages={safetyReportMessagesAsHubThread(messages)}
                draft={replyDraft}
                onDraftChange={setReplyDraft}
                onSubmit={() => {
                  const trimmed = replyDraft.trim();
                  if (trimmed === "") return;
                  void replyMutation.mutateAsync(trimmed);
                }}
                submitting={replyMutation.isPending}
                canReply={!safetyReportStatusIsTerminal(report.status)}
                error={replyError}
                composeLabel="Reply to safety team"
                viewer="reporter"
                staffPartyLabel="Manufacturing Management"
              />
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function SafetyReportsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<SafetyReportListPage />} />
      <Route path="new" element={<SafetyReportNewPage />} />
      <Route path=":id" element={<SafetyReportDetailPage />} />
      <Route path="*" element={<Navigate to="/personal/safety-reports" replace />} />
    </Routes>
  );
}
